import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SelectData } from "../../../../models";
import { searchStandards } from "../../../../service/query";
import { searchWorkflowTemplates } from "../../../../service/query/QueryService.full";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { getWorkflowTemplateRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria } from "../../../../widget";
import { WorkflowDataCardModel } from "./components/WorkflowDataCards/models";
import { getWorkflowTemplateExpiryFilterGroups } from "./utils";

interface UseWorkflowTemplatesReturnData {
  workflowTemplates: WorkflowDataCardModel[];
  relatedProjectWorkflowTemplateUuid: string | undefined;
  isLoadingWorkflowTemplates: boolean;
  projectTypesSelectData: SelectData | undefined;

  projectTypeFilterUuid: string | undefined | null;
  setProjectTypeFilterUuid: Dispatch<SetStateAction<string | undefined | null>>;

  showCreateWorkflowTemplatePanel: boolean;
  setShowCreateWorkflowTemplatePanel: Dispatch<SetStateAction<boolean>>;
  isExpiredFilter: boolean;
  setIsExpiredFilter: Dispatch<SetStateAction<boolean>>;

  setRefreshWorkflowTemplates: Dispatch<SetStateAction<boolean>>;

  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

const pageSize = 6;

export const useWorkflowTemplates = (): UseWorkflowTemplatesReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const [isLoadingWorkflowTemplates, setIsLoadingWorkflowTemplates] = useState(true);

  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowDataCardModel[]>([]);
  const [relatedProjectWorkflowTemplateUuid, setRelatedProjectWorkflowTemplateUuid] = useState<string>();
  const [refreshWorkflowTemplates, setRefreshWorkflowTemplates] = useState(false);

  const [projectTypesSelectData, setProjectTypesSelectData] = useState<SelectData>([]);
  const [projectTypeFilterUuid, setProjectTypeFilterUuid] = useState<string | undefined | null>();
  const [isExpiredFilter, setIsExpiredFilter] = useState<boolean>(false);

  const [showCreateWorkflowTemplatePanel, setShowCreateWorkflowTemplatePanel] = useState(false);
  const isFirstFilterChange = useRef(true);

  const [startCursor, setStartCursor] = useState<string | null>(null);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null);
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);

  const onPrevious = (): void => {
    setBeforeCursor(startCursor);
    setAfterCursor(null);
  };
  const onNext = (): void => {
    setBeforeCursor(null);
    setAfterCursor(endCursor);
  };

  const fetchWorkflowTemplates = useCallback(
    async (onFilterChange: boolean): Promise<void> => {
      setIsLoadingWorkflowTemplates(true);
      setRelatedProjectWorkflowTemplateUuid(undefined);

      // If changing filter, reset pagination
      if (onFilterChange) {
        setBeforeCursor(null);
        setAfterCursor(null);
      }

      const filterCriteria = dataGridMapFilterCriteria([]);
      if (projectTypeFilterUuid)
        filterCriteria.projectType = {
          uuid: {
            operator: "eq",
            value: projectTypeFilterUuid,
          },
        };
      const response = await searchWorkflowTemplates({
        ...getWorkflowTemplateExpiryFilterGroups(isExpiredFilter),
        paging: {
          limit: pageSize,
          beforeCursor: onFilterChange ? null : beforeCursor, // If changing filter, reset pagination
          afterCursor: onFilterChange ? null : afterCursor,
        },
        filter: { results: filterCriteria },
      });

      if (response.status === Status.Success && response.data) {
        setStartCursor(response.data.paging.startCursor);
        setEndCursor(response.data.paging.endCursor);
        setHasPrevious(response.data.paging.hasPreviousPage);
        setHasNext(response.data.paging.hasNextPage);

        setWorkflowTemplates(
          response.data.results.map((workflowTemplate) => ({
            uuid: workflowTemplate.uuid,
            name: workflowTemplate.name,
            description: workflowTemplate.description,
            tasks: workflowTemplate.taskCount,
            organisationName: workflowTemplate.ownerOrganisation.name,
            projectTypeUuid: workflowTemplate.projectType.uuid ?? "",
            isExpired: workflowTemplate.validTo !== null && workflowTemplate.validTo < new Date(),
            onShowProjectsClick: () => setRelatedProjectWorkflowTemplateUuid(workflowTemplate.uuid),
            onViewWorkflowClick: () => navigate(getWorkflowTemplateRoute(workflowTemplate.uuid, currentUserType)),
          }))
        );
      }
    },
    [beforeCursor, afterCursor, isExpiredFilter, projectTypeFilterUuid]
  );

  const fetchProjectTypes = async (): Promise<void> => {
    const response = await searchStandards({
      paging: {
        limit: 50,
        beforeCursor,
        afterCursor,
      },
    });

    if (response.status === Status.Success && response.data) {
      const projectTypeMap = new Map<string, { key: string; value: string }>();

      response.data.results.forEach((standard) => {
        standard.projectTypes.forEach((projectType) => {
          projectTypeMap.set(projectType.uuid, {
            key: projectType.uuid,
            value: projectType.displayName,
          });
        });
      });

      setProjectTypesSelectData(Array.from(projectTypeMap.values()) as SelectData);
    }
  };

  useEffect(() => {
    fetchWorkflowTemplates(false).finally(() => {
      setIsLoadingWorkflowTemplates(false);
    });
  }, [beforeCursor, afterCursor, refreshWorkflowTemplates]);

  useEffect(() => {
    if (isFirstFilterChange.current) {
      isFirstFilterChange.current = false;
      return;
    }

    fetchWorkflowTemplates(true).finally(() => {
      setIsLoadingWorkflowTemplates(false);
    });
  }, [projectTypeFilterUuid, isExpiredFilter]);

  useEffect(() => {
    fetchProjectTypes();
  }, []);

  return {
    workflowTemplates,
    relatedProjectWorkflowTemplateUuid,
    projectTypesSelectData,
    isLoadingWorkflowTemplates,

    projectTypeFilterUuid,
    setProjectTypeFilterUuid,

    showCreateWorkflowTemplatePanel,
    setShowCreateWorkflowTemplatePanel,
    isExpiredFilter,
    setIsExpiredFilter,

    setRefreshWorkflowTemplates,

    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
  };
};
